import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RecommendationRow {
  readonly searchRecommendationId: string;
  readonly recommendationCode: string;
  readonly recommendationType: string;
  readonly sourceModule: string;
  readonly contentType: string;
  readonly targetTitle: string;
  readonly relevanceScore: string;
  readonly reason: string;
  readonly generatedBy: string;
  readonly status: string;
}

const STATUSES = ['GENERATED', 'ACCEPTED', 'DISMISSED', 'EXPIRED'];

/**
 * M39 — Search Recommendation Panel (§16). Proactive discovery: rule- and
 * AI-generated content suggestions (T521) derived from the user's own search
 * behaviour over the ACL-trimmed index. A user generates a fresh set, then
 * accepts (surfacing the item) or dismisses each one — feedback that tunes future
 * suggestions. Viewing needs RECOMMENDATION_VIEW; generating / accepting /
 * dismissing needs RECOMMENDATION_MANAGE (SoD, §27). A user only ever sees their
 * own recommendations; a 403 surfaces the required permission.
 */
export function SearchRecommendationPanel(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RecommendationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('GENERATED');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    apiRequest<{ data: RecommendationRow[]; total: number }>(`/v1/search/recommendations?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const generate = async (): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: RecommendationRow[]; total: number }>(`/v1/search/recommendations/generate`, {
        method: 'POST',
        body: {},
        token,
      });
      setNotice(t('m39.recommendations.generated', { n: r.total }));
      setStatus('GENERATED');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.recommendations.notAuthorisedGenerate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (row: RecommendationRow, verb: 'accept' | 'dismiss'): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/search/recommendations/${row.searchRecommendationId}/${verb}`, { method: 'POST', token });
      setNotice(verb === 'accept' ? t('m39.recommendations.accepted', { code: row.recommendationCode }) : t('m39.recommendations.dismissed', { code: row.recommendationCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.recommendations.notAuthorisedAct'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="search-recommendation-panel">
      <h1>{t('m39.recommendations.title')}</h1>

      <section aria-label="reco-actions">
        <Button variant="primary" type="button" aria-label="generate-reco" onClick={() => void generate()} disabled={busy}>
          {busy ? t('m39.recommendations.generating') : t('m39.recommendations.generate')}
        </Button>
        <label>{t('m39.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {actionError && <p role="alert" aria-label="reco-error">{actionError}</p>}
        {notice && <p aria-label="reco-notice">{notice}</p>}
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="reco-total">{t('m39.recommendations.total', { total })}</p>
          {rows.length === 0 ? (
            <p aria-label="reco-empty">{t('m39.recommendations.empty')}</p>
          ) : (
            <ul aria-label="reco-list">
              {rows.map((r) => (
                <li key={r.searchRecommendationId} data-reco={r.recommendationCode}>
                  <strong>{r.targetTitle}</strong>
                  <span aria-label={`type-${r.recommendationCode}`}>{r.recommendationType}</span>
                  <span aria-label={`module-${r.recommendationCode}`}>{r.sourceModule}</span>
                  <span aria-label={`score-${r.recommendationCode}`}>{decimal(r.relevanceScore)}</span>
                  <span aria-label={`by-${r.recommendationCode}`}>{r.generatedBy}</span>
                  <p>{r.reason}</p>
                  {r.status === 'GENERATED' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`accept-${r.recommendationCode}`} onClick={() => void act(r, 'accept')}>{t('m39.recommendations.accept')}</Button>
                      <Button variant="secondary" type="button" aria-label={`dismiss-${r.recommendationCode}`} onClick={() => void act(r, 'dismiss')}>{t('m39.recommendations.dismiss')}</Button>
                    </>
                  )}
                  {r.status !== 'GENERATED' && <span aria-label={`status-${r.recommendationCode}`}>{r.status}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
